import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

/**
 * Controlled tab strip (detail-page spec §3) — replaces react-bootstrap's
 * uncontrolled `Tabs` so the active tab is readable (for URL sync) and every
 * pane isn't forced to mount at once.
 *
 * Keyboard: Left/Right moves and activates the adjacent enabled tab, Home/End
 * jump to the first/last. Overflow scrolls with hidden scrollbars, and the
 * active tab is brought into view on mount/change via `scrollLeft` math (not
 * `scrollIntoView`, which can scroll ancestors too).
 */
function TabNav({ tabs, current, onChange, idPrefix = 'tab' }) {
	const containerRef = useRef(null);
	const tabRefs = useRef({});

	useEffect(() => {
		const container = containerRef.current;
		const activeEl = tabRefs.current[current];
		if (!container || !activeEl) {
			return;
		}
		const containerRect = container.getBoundingClientRect();
		const activeRect = activeEl.getBoundingClientRect();
		if (activeRect.left < containerRect.left) {
			container.scrollLeft -= containerRect.left - activeRect.left + 24;
		} else if (activeRect.right > containerRect.right) {
			container.scrollLeft += activeRect.right - containerRect.right + 24;
		}
	}, [current]);

	const handleKeyDown = (e, index) => {
		const enabledIndexes = tabs.map((t, i) => i).filter((i) => !tabs[i].disabled);
		const currentPos = enabledIndexes.indexOf(index);
		let targetIndex = null;
		if (e.key === 'ArrowRight') {
			targetIndex = enabledIndexes[(currentPos + 1) % enabledIndexes.length];
		} else if (e.key === 'ArrowLeft') {
			targetIndex = enabledIndexes[(currentPos - 1 + enabledIndexes.length) % enabledIndexes.length];
		} else if (e.key === 'Home') {
			targetIndex = enabledIndexes[0];
		} else if (e.key === 'End') {
			targetIndex = enabledIndexes[enabledIndexes.length - 1];
		}
		if (targetIndex !== null) {
			e.preventDefault();
			const targetTab = tabs[targetIndex];
			onChange(targetTab.id);
			tabRefs.current[targetTab.id]?.focus();
		}
	};

	return (
		<div
			ref={containerRef}
			role='tablist'
			className='tw:flex tw:overflow-x-auto tw:border-b tw:border-border tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden'
		>
			{tabs.map((tab, index) => {
				const isActive = tab.id === current;
				return (
					<button
						key={tab.id}
						ref={(el) => {
							tabRefs.current[tab.id] = el;
						}}
						type='button'
						role='tab'
						id={`${idPrefix}-${tab.id}`}
						aria-selected={isActive}
						aria-controls={`${idPrefix}panel-${tab.id}`}
						tabIndex={isActive ? 0 : -1}
						disabled={tab.disabled}
						onClick={() => onChange(tab.id)}
						onKeyDown={(e) => handleKeyDown(e, index)}
						className={classnames(
							'tw:inline-flex tw:shrink-0 tw:items-center tw:gap-[7px] tw:whitespace-nowrap tw:border-0 tw:border-b-2 tw:bg-transparent tw:px-4 tw:pb-3.5 tw:pt-3 tw:text-[13.5px] tw:font-medium tw:-mb-px tw:transition-colors tw:duration-100',
							isActive ? 'tw:border-accent tw:text-accent tw:font-semibold' : 'tw:border-transparent tw:text-ink-2 tw:hover:text-ink-0',
						)}
					>
						{tab.icon}
						{tab.label}
						{typeof tab.count === 'number' && (
							<span
								aria-label={`${tab.count} ${tab.countLabel || ''}`.trim()}
								className={classnames(
									'tw:rounded-[10px] tw:px-1.5 tw:py-0.5 tw:text-[11px] tw:font-semibold tw:tabular-nums',
									isActive ? 'tw:bg-accent-soft tw:text-accent' : 'tw:bg-surface-3 tw:text-ink-2',
								)}
							>
								{tab.count}
							</span>
						)}
					</button>
				);
			})}
		</div>
	);
}

TabNav.propTypes = {
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.node.isRequired,
			icon: PropTypes.node,
			count: PropTypes.number,
			countLabel: PropTypes.string,
			disabled: PropTypes.bool,
		}),
	).isRequired,
	current: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	idPrefix: PropTypes.string,
};

export default TabNav;
